import React, { useState } from 'react';

interface TodoItem {
  id: string;
  category: 'MVP' | 'Core' | 'Enhancement' | 'Future';
  priority: 'critical' | 'high' | 'medium' | 'low';
  title: string;
  description: string;
  status: 'not-started' | 'in-progress' | 'completed' | 'blocked';
  dependencies?: string[];
  estimatedHours?: number;
  technicalNotes?: string;
}

const FUTRBET_TODOS: TodoItem[] = [
  // ============ MVP - Critical for Launch ============
  {
    id: 'auth-1',
    category: 'MVP',
    priority: 'critical',
    title: 'User Authentication System',
    description: 'Implement magic link email authentication for user accounts',
    status: 'not-started',
    estimatedHours: 8,
    technicalNotes: 'Use CloudFlare Workers Email + D1 for session management'
  },
  {
    id: 'user-1',
    category: 'MVP',
    priority: 'critical',
    title: 'User Profile & Balance Management',
    description: 'Create user profiles with virtual currency balance tracking',
    status: 'not-started',
    dependencies: ['auth-1'],
    estimatedHours: 6,
    technicalNotes: 'Store in D1 users table, start with 1000 virtual credits'
  },
  {
    id: 'bet-1',
    category: 'MVP',
    priority: 'critical',
    title: 'Betting Engine',
    description: 'Implement actual bet placement with balance deduction and share calculation',
    status: 'not-started',
    dependencies: ['user-1'],
    estimatedHours: 10,
    technicalNotes: 'Use CPMM (Constant Product Market Maker) for probability calculations'
  },
  {
    id: 'resolution-1',
    category: 'MVP',
    priority: 'critical',
    title: 'Market Resolution System',
    description: 'Allow market creators to resolve markets and distribute winnings',
    status: 'not-started',
    dependencies: ['bet-1'],
    estimatedHours: 8,
    technicalNotes: 'Calculate payouts based on shares owned, update user balances'
  },
  {
    id: 'search-1',
    category: 'MVP',
    priority: 'high',
    title: 'Market Search & Discovery',
    description: 'Add search bar and category filters for finding markets',
    status: 'not-started',
    estimatedHours: 4,
    technicalNotes: 'Full-text search in D1, category tags'
  },

  // ============ Core Features ============
  {
    id: 'leaderboard-1',
    category: 'Core',
    priority: 'high',
    title: 'Leaderboard Implementation',
    description: 'Show top predictors by profit, win rate, and streak',
    status: 'not-started',
    dependencies: ['resolution-1'],
    estimatedHours: 6,
    technicalNotes: 'Cache in leaderboard table, update on bet resolution'
  },
  {
    id: 'portfolio-1',
    category: 'Core',
    priority: 'high',
    title: 'Enhanced Portfolio View',
    description: 'Show active bets, pending resolutions, and historical performance',
    status: 'not-started',
    dependencies: ['bet-1'],
    estimatedHours: 5,
    technicalNotes: 'Group by status, show potential payouts'
  },
  {
    id: 'comments-1',
    category: 'Core',
    priority: 'medium',
    title: 'Market Comments/Discussion',
    description: 'Add commenting system for market discussions',
    status: 'not-started',
    dependencies: ['auth-1'],
    estimatedHours: 6,
    technicalNotes: 'Store in comments table, support threading'
  },
  {
    id: 'notifications-1',
    category: 'Core',
    priority: 'medium',
    title: 'Email Notifications',
    description: 'Notify users of market resolution, new bets on their markets',
    status: 'not-started',
    dependencies: ['auth-1', 'resolution-1'],
    estimatedHours: 8,
    technicalNotes: 'Use CloudFlare Email Workers'
  },
  {
    id: 'categories-1',
    category: 'Core',
    priority: 'medium',
    title: 'Market Categories',
    description: 'Add predefined categories (Sports, Crypto, Politics, Entertainment, Tech)',
    status: 'not-started',
    estimatedHours: 3,
    technicalNotes: 'Add category field to markets table'
  },

  // ============ Enhancements ============
  {
    id: 'oracle-1',
    category: 'Enhancement',
    priority: 'medium',
    title: 'Oracle Integration for Auto-Resolution',
    description: 'Connect to public APIs for automatic market resolution',
    status: 'not-started',
    dependencies: ['resolution-1'],
    estimatedHours: 12,
    technicalNotes: 'CoinGecko for crypto, ESPN for sports, etc.'
  },
  {
    id: 'charts-1',
    category: 'Enhancement',
    priority: 'medium',
    title: 'Probability Charts',
    description: 'Show historical probability changes over time',
    status: 'not-started',
    dependencies: ['bet-1'],
    estimatedHours: 8,
    technicalNotes: 'Use Chart.js, store probability snapshots'
  },
  {
    id: 'mobile-1',
    category: 'Enhancement',
    priority: 'low',
    title: 'Mobile App',
    description: 'Create React Native mobile app',
    status: 'not-started',
    estimatedHours: 40,
    technicalNotes: 'Share components with web, use same API'
  },
  {
    id: 'social-1',
    category: 'Enhancement',
    priority: 'low',
    title: 'Social Features',
    description: 'Follow users, share markets on social media',
    status: 'not-started',
    dependencies: ['auth-1'],
    estimatedHours: 10,
    technicalNotes: 'Add follows table, OpenGraph tags'
  },
  {
    id: 'analytics-1',
    category: 'Enhancement',
    priority: 'low',
    title: 'Advanced Analytics',
    description: 'Market creator dashboard with detailed statistics',
    status: 'not-started',
    dependencies: ['bet-1', 'resolution-1'],
    estimatedHours: 12,
    technicalNotes: 'Volume over time, user demographics, bet distribution'
  },

  // ============ Future Features ============
  {
    id: 'crypto-1',
    category: 'Future',
    priority: 'low',
    title: 'Cryptocurrency Integration',
    description: 'Allow betting with actual cryptocurrency',
    status: 'not-started',
    estimatedHours: 30,
    technicalNotes: 'Web3 wallet integration, smart contracts'
  },
  {
    id: 'api-1',
    category: 'Future',
    priority: 'low',
    title: 'Public API',
    description: 'Create public API for developers',
    status: 'not-started',
    estimatedHours: 20,
    technicalNotes: 'API key management, rate limiting, documentation'
  },
  {
    id: 'conditional-1',
    category: 'Future',
    priority: 'low',
    title: 'Conditional Markets',
    description: 'Markets that resolve based on other market outcomes',
    status: 'not-started',
    dependencies: ['resolution-1'],
    estimatedHours: 15,
    technicalNotes: 'Complex resolution logic, dependency tracking'
  },
  {
    id: 'liquidity-1',
    category: 'Future',
    priority: 'low',
    title: 'Automated Market Maker',
    description: 'Add liquidity pools for better pricing',
    status: 'not-started',
    dependencies: ['bet-1'],
    estimatedHours: 20,
    technicalNotes: 'Implement LMSR (Logarithmic Market Scoring Rule)'
  }
];

export default function DevModeTodoList() {
  const [expandedCategory, setExpandedCategory] = useState<string | null>('MVP');
  const [selectedItem, setSelectedItem] = useState<TodoItem | null>(null);

  const categories = ['MVP', 'Core', 'Enhancement', 'Future'] as const;

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'MVP': return 'from-red-500 to-orange-500';
      case 'Core': return 'from-green-500 to-emerald-500';
      case 'Enhancement': return 'from-blue-500 to-indigo-500';
      case 'Future': return 'from-purple-500 to-pink-500';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'critical': return 'bg-red-500 text-white animate-pulse';
      case 'high': return 'bg-orange-500 text-white';
      case 'medium': return 'bg-yellow-500 text-black';
      case 'low': return 'bg-gray-400 text-white';
      default: return 'bg-gray-300';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return '✅';
      case 'in-progress': return '🔄';
      case 'blocked': return '🚫';
      default: return '⭕';
    }
  };

  const todosByCategory = categories.reduce((acc, cat) => {
    acc[cat] = FUTRBET_TODOS.filter(todo => todo.category === cat);
    return acc;
  }, {} as Record<string, TodoItem[]>);

  const totalHours = FUTRBET_TODOS.reduce((sum, todo) => sum + (todo.estimatedHours || 0), 0);
  const completedCount = FUTRBET_TODOS.filter(t => t.status === 'completed').length;
  const criticalCount = FUTRBET_TODOS.filter(t => t.priority === 'critical' && t.status !== 'completed').length;

  return (
    <div className="fixed left-4 top-20 w-96 max-h-[80vh] bg-black/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-green-500/30 overflow-hidden z-50">
      <div className="bg-gradient-to-r from-green-600 to-orange-600 p-4">
        <h2 className="text-xl font-bold text-white flex items-center justify-between">
          <span>🚀 FutrBet Development Roadmap</span>
          <span className="text-sm bg-white/20 px-2 py-1 rounded">DevMode</span>
        </h2>
        <div className="mt-2 grid grid-cols-3 gap-2 text-xs text-white/90">
          <div className="bg-white/10 rounded p-1 text-center">
            <div className="font-bold">{completedCount}/{FUTRBET_TODOS.length}</div>
            <div>Complete</div>
          </div>
          <div className="bg-white/10 rounded p-1 text-center">
            <div className="font-bold">{criticalCount}</div>
            <div>Critical</div>
          </div>
          <div className="bg-white/10 rounded p-1 text-center">
            <div className="font-bold">~{totalHours}h</div>
            <div>Estimated</div>
          </div>
        </div>
      </div>

      <div className="max-h-[60vh] overflow-y-auto p-4">
        {categories.map(category => (
          <div key={category} className="mb-4">
            <button
              onClick={() => setExpandedCategory(expandedCategory === category ? null : category)}
              className={`w-full p-3 rounded-lg bg-gradient-to-r ${getCategoryColor(category)} text-white font-bold flex items-center justify-between hover:opacity-90 transition-all`}
            >
              <span className="flex items-center gap-2">
                {category}
                <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">
                  {todosByCategory[category].length}
                </span>
              </span>
              <svg
                className={`w-5 h-5 transition-transform ${expandedCategory === category ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {expandedCategory === category && (
              <div className="mt-2 space-y-2">
                {todosByCategory[category].map(todo => (
                  <div
                    key={todo.id}
                    onClick={() => setSelectedItem(todo)}
                    className="bg-gray-800/50 hover:bg-gray-700/50 rounded-lg p-3 cursor-pointer transition-all border border-gray-700 hover:border-green-500/50"
                  >
                    <div className="flex items-start justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{getStatusIcon(todo.status)}</span>
                        <h4 className="text-white font-semibold text-sm">{todo.title}</h4>
                      </div>
                      <span className={`px-2 py-0.5 rounded text-xs font-bold ${getPriorityBadge(todo.priority)}`}>
                        {todo.priority}
                      </span>
                    </div>
                    <p className="text-gray-400 text-xs mb-2">{todo.description}</p>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      {todo.estimatedHours && (
                        <span className="flex items-center gap-1">
                          ⏱️ {todo.estimatedHours}h
                        </span>
                      )}
                      {todo.dependencies && todo.dependencies.length > 0 && (
                        <span className="flex items-center gap-1">
                          🔗 {todo.dependencies.length} deps
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Detail Modal */}
      {selectedItem && (
        <div className="absolute inset-0 bg-black/90 backdrop-blur-xl flex items-center justify-center p-4 z-60">
          <div className="bg-gray-900 rounded-xl p-6 max-w-sm w-full border border-green-500/30">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-bold text-white">{selectedItem.title}</h3>
              <button
                onClick={() => setSelectedItem(null)}
                className="text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <div className="text-xs text-gray-400 mb-1">Description</div>
                <div className="text-sm text-white">{selectedItem.description}</div>
              </div>

              {selectedItem.technicalNotes && (
                <div>
                  <div className="text-xs text-gray-400 mb-1">Technical Notes</div>
                  <div className="text-sm text-green-400 font-mono bg-gray-800 p-2 rounded">
                    {selectedItem.technicalNotes}
                  </div>
                </div>
              )}

              {selectedItem.dependencies && selectedItem.dependencies.length > 0 && (
                <div>
                  <div className="text-xs text-gray-400 mb-1">Dependencies</div>
                  <div className="flex flex-wrap gap-1">
                    {selectedItem.dependencies.map(dep => {
                      const depItem = FUTRBET_TODOS.find(t => t.id === dep);
                      return (
                        <span key={dep} className="bg-gray-700 px-2 py-1 rounded text-xs text-white">
                          {depItem?.title || dep}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex justify-between items-center pt-3 border-t border-gray-700">
                <span className={`px-3 py-1 rounded text-xs font-bold ${getPriorityBadge(selectedItem.priority)}`}>
                  {selectedItem.priority.toUpperCase()}
                </span>
                <span className="text-sm text-gray-400">
                  Est: {selectedItem.estimatedHours || '?'} hours
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="p-3 border-t border-gray-700 bg-gray-900">
        <div className="text-xs text-gray-400 text-center">
          💡 Click items for details • Build in order for best results
        </div>
      </div>
    </div>
  );
}